import { getPathways } from '@/api/pathways';
import { SYSTEM_STATUS } from '@/consts';
import { deepMerge } from '@/utils/miscUtils';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

const initialValues = {
  project: null,
  pathways: null,
  systems: null,
  comparisons: null,
};

const useResourcesStore = create()(immer(() => initialValues));
const { setState: set, getState: get } = useResourcesStore;

// actions
export const resourcesActions = {
  setResources: resources => set(resources),
  setPathways: pathways => set({ pathways }),
  setSystems: systems => set({ systems }),
  getPathways: () => get().pathways?.results,
  getSystems: () => get().systems?.results,
  getSucceededSystems: () => get().systems?.results?.filter(s => s.status === SYSTEM_STATUS.succeeded),
  removePathways: pathwayIds => {
    const pathwayIdSet = new Set(pathwayIds);

    set(state => {
      if (!state.pathways?.results) return;
      state.pathways.results = state.pathways.results.filter(pathway => !pathwayIdSet.has(pathway.id));
    });
  },
  removeSystems: systemIds => {
    const systemIdSet = new Set(systemIds);

    set(state => {
      if (!state.systems?.results) return;
      state.systems.results = state.systems.results.filter(system => !systemIdSet.has(system.id));
    });
  },

  updatePathway: params => {
    const { pathways } = get();
    const index = pathways?.results?.findIndex(p => p.id === params.id);

    if (index >= 0) {
      const pathway = deepMerge(pathways?.results?.[index], params);

      set(state => {
        state.pathways.results[index] = pathway;
      });
    }
  },

  updateSystem: params => {
    const { systems } = get();
    const index = systems?.results?.findIndex(s => s.id === params.id);

    if (index >= 0) {
      const system = deepMerge(systems?.results?.[index], params);

      set(state => {
        state.systems.results[index] = system;
      });
    }
  },
  loadMorePathways: async projectId => {
    const { pathways } = get();
    const next = pathways?.next;

    if (next) {
      const url = new URL(next);
      const params = new URLSearchParams(url.search);
      const offset = params.get('offset');
      const limit = params.get('limit');
      const pathways = await getPathways(projectId, { offset, limit });
      set(state => {
        state.pathways.results = [...state.pathways.results, ...pathways.data.results];
        state.pathways.next = pathways.data.next;
      });
    } else {
      set(state => {
        state.pathways.next = null;
      });
    }
  },
};

export const usePathways = () => useResourcesStore(store => store.pathways?.results);
export const useSystems = () => useResourcesStore(store => store.systems?.results);
export const useNextPathways = () => useResourcesStore(store => store.pathways?.next);
export const useResources = () => useResourcesStore(store => store);

export default useResourcesStore;
